import type { ExternalNavItem, NavItem, PrimaryNavItem, SetupSection } from './types'

export const ACCOUNT_SETUP_SECTION_TITLE = 'Account Setup' as const
export const SETTINGS_ROUTE = '/settings' as const

export const PRICING_HUB_NAV_LABEL = 'Pricing Hub' as const

export const isExternalNavItem = (item: PrimaryNavItem): item is ExternalNavItem =>
  'external' in item && item.external === true

export const buildPrimaryNavigationItems = (
  hasUnpublishedChanges: boolean,
  pricingHubHref?: string,
): PrimaryNavItem[] =>
  [
    {
      label: 'Unpublished Changes',
      icon: 'mdi-arrow-right-bold-box',
      to: '/unpublished',
      showWhen: hasUnpublishedChanges,
    },
    {
      label: 'Dashboard',
      icon: 'mdi-view-dashboard',
      to: '/',
    },
    {
      label: 'Inventory',
      icon: 'mdi-car-multiple',
      to: '/inventory',
    },
    {
      label: 'Rules & Setup',
      icon: 'mdi-tune-variant',
      to: '/setup',
    },
    getSettingsNavigationItem(),
    {
      label: PRICING_HUB_NAV_LABEL,
      icon: 'mdi-tag-multiple-outline',
      href: pricingHubHref ?? '',
      external: true as const,
      showWhen: Boolean(pricingHubHref),
    },
  ].filter((item) => item.showWhen !== false)

export const setupNavigationSections: SetupSection[] = [
  {
    title: 'Inventory Rules',
    description: 'Control how inventory is organized and published.',
    links: [
      {
        label: 'Groups',
        description: 'Organize vehicles into inventory groups.',
        icon: 'mdi-layers-triple-outline',
        to: '/segments',
      },
      {
        label: 'Publish Rules',
        description: 'Control which vehicles are eligible to publish or show online.',
        icon: 'mdi-format-list-checks',
        to: '/listing',
      },
    ],
  },
  {
    title: 'Feed Setup',
    description: 'Control how feed data becomes inventory data.',
    links: [
      {
        label: 'Vehicle State Feed Rules',
        description: 'Choose which feed supplies vehicles for each condition and vehicle state.',
        icon: 'mdi-database-cog-outline',
        to: '/feed-source-configuration',
      },
      {
        label: 'Field Mappings',
        description: 'Map feed fields into inventory fields.',
        icon: 'mdi-file-tree',
        to: '/mapping',
      },
      {
        label: 'Feed Field Rules',
        description: 'Apply rules to feed-provided values.',
        icon: 'mdi-swap-horizontal',
        to: '/feed-condition-rules',
      },
      {
        label: 'Pre-Processor',
        description: 'Configure feed preparation before inventory mapping.',
        icon: 'mdi-link-variant',
        to: '/group-site',
      },
    ],
  },
  {
    title: ACCOUNT_SETUP_SECTION_TITLE,
    description: 'Manage dealer-level configuration.',
    links: [
      {
        label: 'Settings',
        description: 'Manage account-level inventory settings.',
        icon: 'mdi-cog-outline',
        to: SETTINGS_ROUTE,
      },
    ],
  },
]

export const rulesSetupNavigationSections = setupNavigationSections.filter(
  (section) => section.title !== ACCOUNT_SETUP_SECTION_TITLE,
)

export const rulesSetupRoutes = new Set<string>([
  '/setup',
  ...rulesSetupNavigationSections.flatMap((section) => section.links.map((link) => link.to)),
])

export function getSettingsNavigationItem(): NavItem {
  const accountSetupSection = setupNavigationSections.find(
    (section) => section.title === ACCOUNT_SETUP_SECTION_TITLE,
  )
  const settingsLink = accountSetupSection?.links[0]

  return {
    label: 'Settings',
    icon: settingsLink?.icon ?? 'mdi-cog-outline',
    to: SETTINGS_ROUTE,
  }
}

export function isRulesSetupNavActive(path: string): boolean {
  return rulesSetupRoutes.has(path)
}

export function isSettingsNavActive(path: string): boolean {
  return path === SETTINGS_ROUTE
}
